import { sparkPath, type Point } from "../lib/processing";

/* A small line, 120 by 24, drawn inline. The first and last values are text
 * beside it and the low and high are its title, so the shape is never the
 * only carrier of the fact (docs/design-standards.md). `top` lets every
 * sparkline in a row or a column share one scale. Fewer than two points
 * draws nothing and says so. `stacked` puts the figures under the line, so a
 * table column stays as narrow as the line itself. */
export function Sparkline({ points, top, width = 120, height = 24, format, stacked = false, summary }: {
  points: Point[];
  top?: number;
  width?: number;
  height?: number;
  format?: (n: number) => string;
  stacked?: boolean;
  /* The figure to print beside the line in place of its first and last
     values. A total over the window, where the ends would read as a range
     (James, 2026-10-08). */
  summary?: string;
}) {
  if (points.length < 2) return <span className="muted spark-empty">No history yet</span>;
  const { d, min, max } = sparkPath(points, width, height, top);
  const f = format ?? ((n: number) => Math.round(n).toLocaleString());
  const first = points[0]?.value ?? 0;
  const last = points.at(-1)?.value ?? 0;
  const ends = summary ?? `${f(first)} to ${f(last)}`;
  return (
    <span className={stacked ? "spark stacked" : "spark"}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img"
           aria-label={`${ends}, low ${f(min)}, high ${f(max)}`}>
        <title>{`Low ${f(min)}, high ${f(max)}`}</title>
        <path d={d} fill="none" stroke="currentColor" strokeWidth="1.2" />
      </svg>
      <span className={summary ? "spark-ends" : "muted spark-ends"}>{ends}</span>
    </span>
  );
}
